import React, {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {Collapse, IconButton, List, ListItem, ListItemButton, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {getAllFarms} from "../../../services/farmService";
import {Company} from "../../../types/domain/company";
import {Farm} from "../../../types/domain/farm";
import {FarmListItem} from "./FarmListItem";
import {Field} from "../../../types/domain/field";
import {useNavigate, useParams} from "react-router-dom";
import {useAtom, useAtomValue} from "jotai";
import {activeMergedFarmIds} from "../../../store/activeMergedFarmIds";
import {MaderaStartYear} from "../../../utils/maderaUtils";
import {selectedEntityAtom} from "../../../store/filterSelectionStore";
import {activeYearAtom} from "../../../store/activeYearAtom";

function generateYearsArray(startYear: number): number[] {
    const currentYear = dayjs().year();
    return Array.from({length: currentYear - startYear + 1}, (_, i) => startYear + i).reverse();
}

export interface Props {
    parentCompany: Company;
    activeFieldsFilter: boolean;
}

interface FarmAlert {
    cropAlerts: number;
    moistureAlerts: number;
}

interface FarmListProps {
    farms: Farm[];
    farmsAlerts: Record<string, FarmAlert>;
    companyId: string;
    fieldNameFilter: string;
    foundFieldsByFarmId: Record<string, Field[]>;
    isSearching: boolean;
    fieldCropFilter: string | undefined;
    activeFieldsFilter: boolean;
    basePath: "insights" | "map";
}

export const YearsList: React.FC<Props & FarmListProps> = ({
    parentCompany,
    activeFieldsFilter,
    farmsAlerts,
    fieldNameFilter,
    foundFieldsByFarmId,
    isSearching,
    fieldCropFilter,
    basePath,
    companyId,
}) => {
    const {farmId, fieldId} = useParams();
    const years = useMemo(() => generateYearsArray(MaderaStartYear), []);
    const [openYears, setOpenYears] = useState<Record<number, boolean>>({});
    const [farmsByYear, setFarmsByYear] = useState<Record<number, Farm[]>>({});
    const [, setActiveMergedFarmIdsAtom] = useAtom(activeMergedFarmIds);
    const navigate = useNavigate();
    const [activeYear, setActiveYear] = useAtom(activeYearAtom);
    const selectedEntity = useAtomValue(selectedEntityAtom);

    const toggleYear = (year: number) => {
        setOpenYears((prev) => ({...prev, [year]: !prev[year]}));
        void getFarms(year);
    };

    async function getFarms(year: number) {
        const farms = await getAllFarms(parentCompany.id, activeFieldsFilter, true, year);
        setFarmsByYear({
            ...farmsByYear,
            [year]: farms,
        });
    }

    async function activateYear(year: number) {
        setActiveYear(year);
        const farms = await getAllFarms(parentCompany.id, activeFieldsFilter, true, year);
        setActiveMergedFarmIdsAtom(farms.map((f) => f.id));
        navigate({
            pathname: `/${basePath}/${companyId}/farm/${farms[0].id}`,
            search: location.search,
        });
    }

    async function getAllYears() {
        const results = await Promise.all(
            years.map((year) => getAllFarms(parentCompany.id, activeFieldsFilter, true, year)),
        );

        const newFarmsByYear: Record<number, Farm[]> = years.reduce(
            (acc, year, index) => {
                acc[year] = results[index];
                return acc;
            },
            {} as Record<number, Farm[]>,
        );

        setFarmsByYear((prev) => ({...prev, ...newFarmsByYear}));
    }

    useEffect(() => {
        void getAllYears();

        if (isSearching) {
            years?.forEach((year) => {
                openYears[year] = true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearching, openYears, years]);

    function yearIsActive(year: number) {
        return activeYear === year && selectedEntity?.entityType !== "company";
    }

    return (
        <List component="nav" style={{overflowY: "auto"}}>
            {years.map((year) => (
                <div key={year}>
                    <ListItem
                        disablePadding
                        style={{
                            cursor: "pointer",
                            backgroundColor: yearIsActive(year) ? "#5161C2" : "",
                            color: yearIsActive(year) ? "white" : "",
                        }}
                    >
                        <ListItemButton
                            onClick={() => void activateYear(year)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: "0px",
                                minHeight: "39px",
                                borderBottom: "1px solid #E0E0E0",
                            }}
                        >
                            <IconButton size="small">
                                {openYears[year] ? (
                                    <ExpandMoreIcon
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleYear(year);
                                        }}
                                        sx={{
                                            color: yearIsActive(year) ? "white" : "#6476CB",
                                        }}
                                    />
                                ) : (
                                    <ArrowRightIcon
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleYear(year);
                                        }}
                                        sx={{
                                            color: yearIsActive(year) ? "white" : "#6476CB",
                                        }}
                                    />
                                )}
                            </IconButton>
                            <Typography variant="body1" sx={{marginLeft: "8px"}}>
                                {year}
                            </Typography>
                        </ListItemButton>
                    </ListItem>

                    <Collapse in={openYears[year]} timeout="auto" unmountOnExit>
                        {farmsByYear[year]?.map((farm, index) => {
                            return (
                                <FarmListItem
                                    key={index}
                                    farm={farm}
                                    active={!activeYear && farmId === farm.id}
                                    cropAlert={farmsAlerts[farm.id]?.cropAlerts > 0}
                                    moistureAlert={farmsAlerts[farm.id]?.moistureAlerts > 0}
                                    companyId={parentCompany.id}
                                    fieldNameFilter={fieldNameFilter}
                                    activeFieldId={fieldId}
                                    foundFields={foundFieldsByFarmId[farm.id]}
                                    isSearching={isSearching}
                                    activeFieldsFilter={activeFieldsFilter}
                                    fieldCropFilter={fieldCropFilter}
                                    basePath={basePath}
                                    setActiveYear={setActiveYear}
                                    parentCompany={parentCompany}
                                />
                            );
                        })}
                    </Collapse>
                </div>
            ))}
        </List>
    );
};
